import React, {useEffect, useState} from 'react';
import {useAuth0, User} from '@auth0/auth0-react';
import {Navigate, Route, Routes, useLocation} from 'react-router-dom';
import {Box, Button, Flex, Text} from '@chakra-ui/react';
import {Header} from './AppHeader';
import {useQuery} from '@tanstack/react-query';
import {KeyplayApiProvider, useKeyplayApi} from '../../context/ApiContext';
import {LoadingScreen} from './LoadingScreen';
import * as Sentry from '@sentry/react';
import {Onboarding} from '../Onboarding/Onboarding';
import {Welcome} from './Welcome';
import {analytics} from '../../analytics';
import {UserProvider, useUser} from '../../context/UserContext';
import {
  MetadataProvider,
  useMetadata,
  useMetadataQuery,
} from '../../context/MetadataContext';
import {useIsTemporaryMembership} from '../../hooks/api/user';
import {useCustomer} from '../../hooks/api/metadata';
import {Bold} from '../text';
import {useRemoveUserMutation} from '../Debug/hooks/customer';
import {ErrorPage} from './ErrorPage';
import {Title} from '../Title';
import {useOnboardedRoutes} from './useOnboardedRoutes';
import CallbackHandling from '../Integrations/CallbackHandling';

function AppWrapper() {
  const {
    user,
    isAuthenticated,
    isLoading,
    logout,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated || isLoading) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        setToken(token);
      } catch (e) {
        await loginWithRedirect();
      }
    })();
  }, [getAccessTokenSilently, loginWithRedirect, isLoading, isAuthenticated]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace={true} />;
  }

  if (!token) {
    return <LoadingScreen />;
  }

  return (
    <KeyplayApiProvider token={token}>
      <AuthenticatedApp user={user} logout={logout} token={token} />
    </KeyplayApiProvider>
  );
}

const AuthenticatedApp = ({
  user,
  token,
  logout,
}: {
  user: User;
  token: string;
  logout: Function;
}) => {
  const keyplayApi = useKeyplayApi();
  Sentry.setUser({
    email: user.email,
  });

  // Although the user is now authenticated (i.e. logged in), we need to check to see if they are
  // authorized to view the app in the context of a customer.
  const {data, isError, isPending, refetch} = useQuery({
    queryKey: ['authorized'],
    queryFn: () => keyplayApi<{authed: boolean}>('/customer/authorized'),
  });

  useEffect(() => {
    refetch();
  }, [refetch, token]);

  if (isPending) {
    return <LoadingScreen />;
  }

  if (isError) {
    return <ErrorPage />;
  }

  return (
    <>
      {!data?.authed ? (
        // The user is authenticated, but not authorized to access the app
        // because either 1) they are not a member of any customer or 2) they
        // need to select which customer they want to access.
        <UnauthorizedApp logout={logout} user={user} />
      ) : (
        // The user is authenticated and authorized to access the app for a
        // specific customer.
        <UserProvider auth0User={user}>
          <AuthorizedApp token={token} />
        </UserProvider>
      )}
    </>
  );
};

const AuthorizedApp = ({token}: {token: string}) => {
  const metadata = useMetadataQuery();

  if (metadata.isPending) {
    return <LoadingScreen />;
  }

  if (!metadata.data && metadata.isError) {
    return <ErrorPage />;
  }

  const {customer} = metadata.data;
  Sentry.setTags({
    'customer.id': customer._id.toString(),
    'customer.name': customer.name,
  });

  return (
    <MetadataProvider metadata={metadata.data}>
      <Box className="App" h="100%">
        {!customer.onboarded ? (
          <OnboardingRoutes />
        ) : (
          <OnboardedApp token={token} />
        )}
        <TemporaryMembershipBanner />
        <Analytics />
      </Box>
    </MetadataProvider>
  );
};

const OnboardingRoutes = () => {
  return (
    <Routes>
      <Route
        path="/onboarding/*"
        element={
          <Title title="Onboarding">
            <Onboarding />
          </Title>
        }
      />
      <Route path="/oauth-callback" element={<CallbackHandling />} />
      <Route path="*" element={<Navigate to="/onboarding" />} />
    </Routes>
  );
};

const OnboardedApp = ({token}: {token: string}) => {
  const routes = useOnboardedRoutes();

  return (
    <Flex direction="column" h="100%">
      <Header token={token} />
      <Box pt="50px" flexGrow="1">
        <Routes>
          {routes.map(({path, element}) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </Box>
    </Flex>
  );
};

const TemporaryMembershipBanner = () => {
  const user = useUser();
  const customer = useCustomer();
  const {loginWithRedirect} = useAuth0();
  const removeUser = useRemoveUserMutation();

  const isTemporaryMembership = useIsTemporaryMembership();
  if (!isTemporaryMembership) {
    return null;
  }

  return (
    <Flex
      bgColor="kred.300"
      color="kgray.200"
      justifyContent="space-between"
      position="absolute"
      bottom="0"
      left="0"
      right="0"
      p="2"
      zIndex="2"
    >
      <Text>
        You are temporarily logged into <Bold>{customer.name}</Bold>.
      </Text>
      <Button
        colorScheme="white"
        isDisabled={removeUser.isPending}
        onClick={() => {
          removeUser.mutate(
            {
              userId: user.auth0.sub!,
              customerId: customer._id,
            },
            {
              onSuccess: () => {
                loginWithRedirect();
              },
            }
          );
        }}
        variant="link"
      >
        Remove access
      </Button>
    </Flex>
  );
};

const UnauthorizedApp = ({logout, user}: {logout: Function; user: User}) => {
  return (
    <Routes>
      <Route
        path="/welcome"
        element={<Welcome logout={logout} user={user} />}
      />
      <Route path="*" element={<Navigate to="/welcome" />} />
    </Routes>
  );
};

// Dummy component for analytics tracking to reduce app re-renders.
const Analytics = () => {
  const metadata = useMetadata();
  const user = useUser();
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Only call identify when we have all the fields to track.
    if (!metadata.customer || !user.extras.intercomHash) {
      return;
    }

    const {_id, name, plan} = metadata.customer;
    analytics.identify(
      user.auth0.sub,
      {
        email: user.auth0.email,
        name: user.auth0.name,
        company: {
          id: _id.toString(),
          name,
          plan: plan.type,
        },
        lastLogin: new Date().getTime(),
      },
      {
        integrations: {
          Intercom: {
            user_hash: user.extras.intercomHash,
          },
        },
      }
    );
    setInitialized(true);
  }, [metadata.customer, user]);

  useEffect(() => {
    // Wait until we have identified the user before tracking page views.
    if (!initialized) {
      return;
    }

    analytics.page();
  }, [location, initialized]);

  return <></>;
};

export default AppWrapper;
